import type { CSSProperties } from 'react';
import {
  CHAPTER_TOTAL,
  chapterByNumber,
  chapterCountLine,
  segmentFillPercent,
  type ChapterNumber,
  type ChapterProgress,
} from '../../lib/onboardingChapters';
import styles from './ChapterSegments.module.css';

type ChapterSegmentsProps = {
  progress: ChapterProgress;
};

const SEGMENTS: ChapterNumber[] = [1, 2, 3, 4];

/**
 * O1 — the chaptered progress treatment. Four 3px segments plus the quiet
 * within-chapter count, replacing the six-dot `ProgressRail` in the onboarding
 * flow. (`ProgressRail` itself stays: `routes/primitives.tsx` still gallery-
 * renders it, and nothing about that component changed.)
 *
 * Done chapters fill marine, pending sit at 12 percent black on the ambient,
 * and the current chapter fills proportionally to the questions ALREADY behind
 * this athlete. The proportion comes from `segmentFillPercent`, which counts
 * only the screens actually queued — a conditional beat therefore lengthens the
 * chapter instead of making the bar lie.
 *
 * The count line under it never quotes a whole-flow number: the largest figure
 * anyone sees is the length of the chapter they are standing in.
 *
 * Accessibility: the strip is ONE progressbar over the four chapters, and the
 * screen-reader sentence lives in the shell's existing aria-live region rather
 * than here, so a chapter turn is announced once instead of twice.
 */
export function ChapterSegments({ progress }: ChapterSegmentsProps) {
  const countLine = chapterCountLine(progress);
  const chapter = chapterByNumber(progress.n);

  return (
    <div className={styles.wrap}>
      <div
        className={styles.strip}
        role="progressbar"
        aria-valuenow={progress.n}
        aria-valuemin={1}
        aria-valuemax={CHAPTER_TOTAL}
        aria-valuetext={`Chapter ${progress.n} of ${CHAPTER_TOTAL}, ${chapter.name}`}
      >
        {SEGMENTS.map((segment) => (
          <span key={segment} className={styles.segment} aria-hidden>
            <span
              className={styles.fill}
              style={
                { '--seg-fill': `${segmentFillPercent(segment, progress)}%` } as CSSProperties &
                  Record<string, string>
              }
            />
          </span>
        ))}
      </div>
      {countLine && <div className={styles.countLine}>{countLine}</div>}
    </div>
  );
}
